import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useEffect, useMemo, useState } from 'react';

import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { fetchBidIncrementsIfNeeded } from '@/redux/modules/bidIncrements/bidIncrements.actions';
import { getBidIncrementsByItemId } from '@/redux/modules/bidIncrements/bidIncrements.selectors';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { getCatalogIsInstantAutopay } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryBidderMaxBid,
    getItemSummaryCatalogId,
    getItemSummaryLeadingBid,
    getItemSummaryStartPrice,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchItemDetailsIfNeeded } from '@/redux/modules/itemDetail';
import { CompleteAccountModalContext, ModalContext, openPlaceAbsenteeBidModal } from '@/redux/modules/modal';
import { getNextBidMinimum } from '@/utils/absenteeBids';
import { TrackPlaceBidPressedButtonLocation, useTrackPlaceBidPressedAnalytics } from '@/utils/amplitude/analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

type PlaceBidButtonProps = {
    itemId: number;
};

const PlaceBidButton = ({ itemId }: PlaceBidButtonProps) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();
    const isMobile = useIsMobileLayout();
    const shouldUseShortBidText = !useIsWiderThan(868);
    const placeBidText = !isMobile && shouldUseShortBidText ? 'bid' : 'place_bid';
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const bidderMaxBid = useAppSelector((state) => getItemSummaryBidderMaxBid(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));

    const catalogRegistration = useAppSelector((state) => getCatalogRegistration(state, catalogId));
    const { approved, registered } = catalogRegistration;

    const userHasPlacedBid = !isMobile && bidderHasBid;
    const userHasRegisteredForAuction = (approved || registered) && !bidderHasBid;
    const bidIncrements = useAppSelector((state) => getBidIncrementsByItemId(state, itemId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const newBidMinimum = getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0;

    const trackPlaceBidPressedAnalytics = useTrackPlaceBidPressedAnalytics(catalogId, itemId);

    const [bidAmount, setBidAmount] = useState(
        getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0
    );

    const modalContext = useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.placeBid,
            id: itemId,
            itemId,
            type: CompleteAccountModalContext.PlaceBid,
        }),
        [itemId]
    );

    const placeBid = async () => {
        trackPlaceBidPressedAnalytics(TrackPlaceBidPressedButtonLocation.ItemCard);

        await Promise.all([
            dispatch(fetchItemDetailsIfNeeded([itemId])),
            dispatch(fetchBidIncrementsIfNeeded(catalogId)),
            dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] })),
        ]);

        dispatch(
            openPlaceAbsenteeBidModal({
                bidAmount,
                isInstantAutopay,
                itemId,
                modalContext,
            })
        );
    };

    useEffect(() => {
        setBidAmount(newBidMinimum);
    }, [bidIncrements, newBidMinimum]);

    const haveButtonSpanAcrossCard = userHasRegisteredForAuction || userHasPlacedBid;
    const isPlaceBidText = !bidderHasBid;

    return (
        <Button
            className={cn(
                'max-[375px]:w-[123px] mr-2xs w-[63%] xsMax:mr-xs xsMax:w-[122px]',
                haveButtonSpanAcrossCard &&
                    cn(
                        'mr-0 w-full xsMax:mr-0 xsMax:w-[211px]',
                        isPlaceBidText ? 'max-[560px]:w-[123px]' : 'max-[560px]:w-full'
                    )
            )}
            data-testid="place-bid-container"
            onClick={placeBid}
            size="sm"
        >
            {formatMessage([bidderHasBid ? 'increase_bid' : placeBidText])}
        </Button>
    );
};

export default PlaceBidButton;
